import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase } from '../db/database';
import { useAuthStore } from '../stores/auth.store';
import { useToast } from '../components/common/ToastProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import type { UserProfileDocType, BranchDocType } from '../db/schemas';
import { Pencil, Trash2, ShieldCheck, ShieldAlert, Check, UserPlus, Users as UsersIcon, Building2, Key } from 'lucide-react';
import { ALL_PERMISSIONS, type PermissionKey } from '../lib/permissions';
import { supabase as mainSupabase } from '../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { Drawer } from '../components/common/Drawer';
import { DataTable, SyncStateIcon, type ColumnDef } from '../components/common/DataTable';
import { Button } from '../components/common/Button';
import { useDataTable } from '../hooks/useDataTable';

export function Users() {
  const isOwner = useAuthStore((s) => s.isOwner);
  const currentProfile = useAuthStore((s) => s.profile);
  const toast = useToast();
  const [profiles, setProfiles] = useState<UserProfileDocType[]>([]);
  const [branches, setBranches] = useState<BranchDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  // User Creation & Editing Form Drawer State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newNombre, setNewNombre] = useState('');
  const [newCargoLabel, setNewCargoLabel] = useState('');
  const [newIsOwner, setNewIsOwner] = useState(false);
  const [newBranches, setNewBranches] = useState<string[]>([]);
  const [newPerms, setNewPerms] = useState<PermissionKey[]>([]);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOwner) {
      setIsLoading(false);
      return;
    }
    const db = getDatabase();
    if (!db) {
      setIsLoading(false);
      return;
    }

    const subProfiles = db.user_profiles.find({
      selector: { is_deleted: false }
    }).$.subscribe((docs) => {
      setProfiles(docs.map((d) => d.toJSON() as unknown as UserProfileDocType));
      setIsLoading(false);
    });

    const subBranches = db.branches.find({
      selector: { is_deleted: false },
      sort: [{ name: 'asc' }],
    }).$.subscribe((docs) => {
      setBranches(docs.map((d) => d.toJSON()));
    });

    return () => {
      subProfiles.unsubscribe();
      subBranches.unsubscribe();
    };
  }, [isOwner]);

  const tableState = useDataTable({
    data: profiles,
    searchFields: ['name', 'email', 'job_title'],
    initialSortBy: 'name',
    sortOptions: [
      { value: 'name', label: 'Nombre' },
      { value: 'email', label: 'Email' },
      { value: 'job_title', label: 'Cargo' },
      { value: 'created_at', label: 'Fecha' },
    ],
    initialPageSize: '25',
    storageKey: 'depilaway-users-page-size',
  });

  // Optimized O(1) Branch Lookup Map
  const branchesMap = useMemo(
    () => new Map(branches.map((s) => [s.id, s.name])),
    [branches]
  );

  const openModal = () => {
    setNewEmail('');
    setNewPassword('');
    setNewNombre('');
    setNewCargoLabel('Personal de Operaciones');
    setNewIsOwner(false);
    setNewBranches(branches.length > 0 ? [branches[0].id] : []);
    setNewPerms([]);
    setEditingProfileId(null);
    setSaveError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (profile: UserProfileDocType) => {
    setEditingProfileId(profile.id);
    setNewEmail(profile.email || '');
    setNewPassword('');
    setNewNombre(profile.name || '');
    setNewCargoLabel(profile.job_title || 'Personal de Operaciones');
    setNewIsOwner(profile.is_owner || false);
    setNewBranches(profile.assigned_branches || []);
    setNewPerms((profile.permissions as PermissionKey[]) || []);
    setSaveError(null);
    setIsModalOpen(true);
  };

  const toggleBranch = (branchId: string) => {
    setNewBranches((prev) =>
      prev.includes(branchId) ? prev.filter((id) => id !== branchId) : [...prev, branchId]
    );
  };

  const togglePerm = (perm: PermissionKey) => {
    setNewPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setIsSavingUser(true);

    try {
      const db = getDatabase();
      if (!db) throw new Error('Base de datos local no inicializada.');

      if (editingProfileId) {
        const doc = await db.user_profiles.findOne(editingProfileId).exec();
        if (doc) {
          await doc.patch({
            name: newNombre.trim(),
            job_title: newCargoLabel.trim(),
            is_owner: newIsOwner,
            assigned_branches: newBranches,
            permissions: newPerms,
            updated_at: new Date().toISOString(),
          });
          toast.success('Usuario actualizado correctamente.');
        } else {
          throw new Error('No se encontró el perfil de usuario para actualizar.');
        }
      } else {
        // Validación de conexión estricta: Registro de credenciales requiere backend Supabase activo
        if (!navigator.onLine) {
          throw new Error('La creación de usuarios requiere conexión a internet activa para registrar las credenciales en el servidor.');
        }

        if (!mainSupabase) {
          throw new Error('El servicio de sincronización en la nube (Supabase) no está configurado.');
        }

        if (!newEmail.trim() || !newPassword) {
          throw new Error('El correo electrónico y la contraseña son obligatorios.');
        }

        if (newPassword.length < 6) {
          throw new Error('La contraseña debe tener al menos 6 caracteres.');
        }

        // Cliente Auth aislado para no alterar la sesión del administrador activo
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
        const isolatedAuthClient = createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false,
            storageKey: 'depilaway_isolated_user_creation',
          },
        });

        const { data, error } = await isolatedAuthClient.auth.signUp({
          email: newEmail.trim().toLowerCase(),
          password: newPassword,
          options: {
            data: {
              name: newNombre.trim(),
              job_title: newCargoLabel.trim(),
              is_owner: newIsOwner,
              assigned_branches: newBranches,
              permissions: newPerms,
            },
          },
        });

        if (error) {
          let friendlyMsg = error.message;
          const msgLower = error.message.toLowerCase();
          if (msgLower.includes('already registered') || msgLower.includes('already exists')) {
            friendlyMsg = 'Ya existe un usuario registrado con este correo electrónico.';
          } else if (msgLower.includes('password')) {
            friendlyMsg = 'La contraseña no cumple con los requisitos de seguridad (mínimo 6 caracteres).';
          } else if (msgLower.includes('rate limit')) {
            friendlyMsg = 'Límite de intentos alcanzado en el servidor. Por favor espere unos minutos.';
          }
          throw new Error(friendlyMsg);
        }

        if (!data.user?.id) {
          throw new Error('El servidor de autenticación no generó un ID de usuario válido.');
        }

        const newUserId = data.user.id;

        await db.user_profiles.insert({
          id: newUserId,
          email: newEmail.trim().toLowerCase(),
          name: newNombre.trim(),
          job_title: newCargoLabel.trim(),
          is_owner: newIsOwner,
          assigned_branches: newBranches,
          permissions: newPerms,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_deleted: false,
        });

        toast.success('Usuario registrado con éxito. Ya puede iniciar sesión con sus credenciales.');
      }

      setIsModalOpen(false);
    } catch (err: any) {
      console.error('[Users] Error al guardar usuario:', err);
      setSaveError(err.message || 'Error al procesar usuario');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleDeleteUser = (p: UserProfileDocType) => {
    if (p.id === currentProfile?.id) {
      toast.warning('No puedes eliminar tu propio usuario de la sesión activa.', 'Acción Denegada');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Revocar Acceso de Usuario',
      message: `¿Estás seguro de eliminar el acceso para ${p.name || p.email}? El usuario ya no podrá iniciar sesión en la aplicación.`,
      onConfirm: async () => {
        try {
          const db = getDatabase();
          if (!db) return;

          const doc = await db.user_profiles.findOne(p.id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: new Date().toISOString(),
            });
            toast.success('Acceso de usuario eliminado correctamente.');
          }
        } catch (err: any) {
          console.error('[Users] Error al eliminar usuario:', err);
          toast.error('Error al eliminar usuario: ' + (err?.message || 'Error desconocido'));
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Columns definition for Desktop Table View
  const columns: ColumnDef<UserProfileDocType>[] = [
    {
      key: 'usuario',
      header: 'Usuario',
      render: (p) => {
        const isCurrent = p.id === currentProfile?.id;
        return (
          <div>
            <div className="font-bold text-text-primary flex items-center gap-2">
              <span>{p.name || 'Sin name'}</span>
              {isCurrent && (
                <span className="px-2 py-0.5 text-[10px] font-extrabold bg-brand-orange/15 text-brand-orange rounded-full">
                  Tú
                </span>
              )}
            </div>
            <div className="text-xs text-text-tertiary font-mono mt-0.5">{p.email}</div>
          </div>
        );
      },
    },
    {
      key: 'job_title',
      header: 'Cargo / Rol',
      sortable: true,
      render: (p) => (
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
          p.is_owner
            ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
            : 'bg-brand-sky/10 text-brand-sky border border-brand-sky/20'
        }`}>
          {p.is_owner ? <ShieldCheck className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
          <span>{p.is_owner ? 'Administrador Global' : p.job_title || 'Operador'}</span>
        </span>
      ),
    },
    {
      key: 'branches',
      header: 'Sucursales',
      sortable: false,
      render: (p) => {
        const assignedBranchNames = (p.assigned_branches || [])
          .map((id) => branchesMap.get(id))
          .filter(Boolean);

        return p.is_owner ? (
          <span className="text-text-tertiary italic text-xs">Acceso Total a Sedes</span>
        ) : assignedBranchNames.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {assignedBranchNames.map((name) => (
              <span key={name} className="px-2 py-0.5 rounded-md bg-surface-secondary text-text-secondary border border-border text-[11px]">
                {name}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-error italic text-xs">Sin branches asignadas</span>
        );
      },
    },
    {
      key: 'acciones',
      header: 'Acciones',
      sortable: false,
      align: 'right',
      render: (p) => {
        const isCurrent = p.id === currentProfile?.id;
        return (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="xs" icon={Pencil} onClick={() => openEditModal(p)} aria-label="Editar usuario" />
            {!isCurrent && (
              <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDeleteUser(p)} aria-label="Eliminar usuario" />
            )}
          </div>
        );
      },
    },
  ];

  // Custom Render for Mobile Cards (<768px)
  const renderMobileCard = (p: UserProfileDocType) => {
    const isCurrent = p.id === currentProfile?.id;
    const assignedBranchNames = (p.assigned_branches || [])
      .map((id) => branchesMap.get(id))
      .filter(Boolean);

    return (
      <div className="p-4 rounded-2xl border border-border bg-surface shadow-xs space-y-3 hover:border-brand-orange/40 transition-all">
        {/* Header: Name, You badge, Role Badge, Sync State */}
        <div className="flex items-start justify-between gap-2 border-b border-border/40 pb-2.5">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-base text-text-primary leading-snug">{p.name || 'Sin name'}</h3>
              {isCurrent && (
                <span className="px-2 py-0.5 text-[10px] font-extrabold bg-brand-orange/15 text-brand-orange rounded-full">
                  Tú
                </span>
              )}
            </div>
            <p className="text-xs font-mono text-text-tertiary mt-0.5">{p.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <SyncStateIcon state={(p as any).sync_state} />
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
              p.is_owner
                ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                : 'bg-brand-sky/10 text-brand-sky border border-brand-sky/20'
            }`}>
              {p.is_owner ? <ShieldCheck className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
              <span>{p.is_owner ? 'Admin' : p.job_title || 'Operador'}</span>
            </span>
          </div>
        </div>

        {/* Branches Access */}
        <div className="space-y-1">
          <span className="text-[11px] font-bold text-text-tertiary uppercase tracking-wider">Sucursales con Acceso:</span>
          <div>
            {p.is_owner ? (
              <span className="text-xs text-text-secondary font-semibold italic">Acceso Total a Sedes</span>
            ) : assignedBranchNames.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {assignedBranchNames.map((name) => (
                  <span key={name} className="px-2 py-0.5 rounded-lg bg-surface-tertiary text-text-primary border border-border/40 text-xs font-medium">
                    {name}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-error italic">Sin branches asignadas</span>
            )}
          </div>
        </div>

        {/* Permissions Count */}
        {p.permissions && p.permissions.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary pt-1 border-t border-border/30">
            <Key className="h-3.5 w-3.5 text-brand-orange" />
            <span>{p.permissions.length} permiso(s) específico(s) asignado(s)</span>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/30">
          <Button variant="secondary" size="sm" icon={Pencil} onClick={() => openEditModal(p)}>
            Editar
          </Button>
          {!isCurrent && (
            <Button variant="danger" size="sm" icon={Trash2} onClick={() => handleDeleteUser(p)}>
              Eliminar
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!isOwner) {
    return (
      <div className="p-8 text-center bg-surface rounded-2xl border border-border space-y-3">
        <ShieldAlert className="h-12 w-12 text-error mx-auto" />
        <h2 className="text-lg font-bold text-text-primary">Acceso Denegado</h2>
        <p className="text-xs text-text-secondary">Únicamente los Administradores Globales pueden acceder a la gestión de usuarios.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary tracking-tight">Gestión de Usuarios</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Administración de cuentas de acceso, perfiles de operadores y asignación de branches
          </p>
        </div>
        <Button variant="primary" size="md" icon={UserPlus} onClick={openModal} className="self-start sm:self-auto">
          Crear Usuario
        </Button>
      </div>

      {/* Unified DataTable Component */}
      <DataTable<UserProfileDocType>
        data={profiles}
        columns={columns}
        getItemKey={(item) => item.id}
        getSyncState={(item) => (item as any).sync_state}
        tableState={tableState}
        isLoading={isLoading}
        renderMobileCard={renderMobileCard}
        searchPlaceholder="Buscar por name, correo o role..."
        emptyTitle="No hay usuarios adicionales registrados"
        emptyDescription="Crea perfiles para gerentes y operadores asignando sus sedes y permisos correspondientes."
        emptyIcon={UsersIcon}
        actionButton={
          <Button variant="primary" size="sm" icon={UserPlus} onClick={openModal}>
            Crear Primer Usuario
          </Button>
        }
      />

      {/* Reusable Drawer for User Creation / Edit Form */}
      <Drawer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingProfileId ? 'Editar Perfil de Usuario' : 'Crear Nuevo Usuario del Sistema'}
        subtitle="Asigna credenciales, sedes de trabajo y permisos de módulos"
        icon={UserPlus}
        maxWidth="xl"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" form="user-form" isLoading={isSavingUser}>
              {editingProfileId ? 'Actualizar Usuario' : 'Crear Usuario'}
            </Button>
          </>
        }
      >
        <form id="user-form" onSubmit={handleSaveUser} className="space-y-5">
          {saveError && (
            <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-xs font-semibold text-error">
              {saveError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre Completo *</label>
              <input
                required
                type="text"
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                placeholder="Ej. Carlos Mendoza"
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Cargo / Título visible</label>
              <input
                type="text"
                value={newCargoLabel}
                onChange={(e) => setNewCargoLabel(e.target.value)}
                placeholder="Ej. Gerente de Sucursal"
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Correo Electrónico (Login) *</label>
              <input
                required
                disabled={!!editingProfileId}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="carlos@depilaway.com"
                className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs disabled:opacity-60"
              />
            </div>

            {!editingProfileId && (
              <div>
                <label className="block text-xs font-semibold text-text-secondary mb-1">Contraseña Inicial *</label>
                <input
                  required
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                />
              </div>
            )}
          </div>

          {/* Owner Role Check */}
          <div className="p-3.5 rounded-xl bg-surface-tertiary/40 border border-border/60 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-amber-500" />
                <span>Administrador Global (Propietario)</span>
              </div>
              <p className="text-[11px] text-text-tertiary mt-0.5">
                Otorga acceso total a todas las branches, finanzas y configuración del sistema.
              </p>
            </div>
            <input
              type="checkbox"
              checked={newIsOwner}
              onChange={(e) => setNewIsOwner(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand-orange focus:ring-brand-orange cursor-pointer"
            />
          </div>

          {/* Branch Assignment */}
          {!newIsOwner && (
            <div className="space-y-2">
              <label className="block text-xs font-bold text-text-primary">Sucursales Permitidas</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {branches.map((s) => {
                  const isChecked = newBranches.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleBranch(s.id)}
                      className={`flex items-center justify-between p-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                        isChecked
                          ? 'border-brand-sky/40 bg-brand-sky/10 text-brand-sky'
                          : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      <span className="truncate">{s.name}</span>
                      {isChecked && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Permissions Granular Selection */}
          {!newIsOwner && (
            <div className="space-y-2 pt-2 border-t border-border/40">
              <label className="block text-xs font-bold text-text-primary">Permisos Específicos de Módulos</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
                {ALL_PERMISSIONS.map((perm) => {
                  const isChecked = newPerms.includes(perm.key);
                  return (
                    <button
                      key={perm.key}
                      type="button"
                      onClick={() => togglePerm(perm.key)}
                      className={`flex items-start gap-2 p-2.5 rounded-xl border text-left text-xs transition-all cursor-pointer ${
                        isChecked
                          ? 'border-brand-orange/40 bg-brand-orange/10 text-brand-orange font-semibold'
                          : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      <div className={`mt-0.5 h-3.5 w-3.5 rounded flex items-center justify-center shrink-0 border ${isChecked ? 'bg-brand-orange border-brand-orange text-white' : 'border-border'}`}>
                        {isChecked && <Check className="h-2.5 w-2.5" />}
                      </div>
                      <div>
                        <div className="font-bold leading-tight">{perm.label}</div>
                        <div className="text-[10px] opacity-75 leading-tight">{perm.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </form>
      </Drawer>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Revocar Acceso"
      />
    </div>
  );
}
